import { Button, Modal } from "@etudescarre/ui";
import { compareLotNumbers, isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  confirmDiscardDraft,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";
import { describePerformanceDeletion } from "./performance-deletion";
import {
  type Lot,
  type LotDraft,
  type LotPerformance,
  type NewLotRow,
  type NewPerformanceRow,
  type PerformanceDraft,
  YardLotsTable,
} from "./yard-lots-table";

function sortLots(lots: Lot[]): Lot[] {
  return [...lots].sort((a, b) => compareLotNumbers(a.nLot, b.nLot));
}

function sortPerformances(performances: LotPerformance[]): LotPerformance[] {
  return [...performances].sort((a, b) => a.order - b.order);
}

function generateTempId(): string {
  return `tmp-${Math.random().toString(36).slice(2)}`;
}

export type YardLotsProps = {
  yardId: number;
  /** Whether the yard is still in its preparation period. */
  inPreparation: boolean;
};

export function YardLots({ yardId, inPreparation }: YardLotsProps) {
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft edits to existing lots/performances: staged until "Enregistrer".
  const [draftLots, setDraftLots] = useState<Map<number, LotDraft>>(new Map());
  const [draftPerformances, setDraftPerformances] = useState<
    Map<number, PerformanceDraft>
  >(new Map());

  // Rows added via "+" but not yet created on the server.
  const [newLots, setNewLots] = useState<NewLotRow[]>([]);
  const [newPerformancesByLot, setNewPerformancesByLot] = useState<
    Map<number, NewPerformanceRow[]>
  >(new Map());

  const [isSaving, setIsSaving] = useState(false);

  const [lotToDelete, setLotToDelete] = useState<Lot | null>(null);
  const [performanceToDelete, setPerformanceToDelete] =
    useState<LotPerformance | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Confirmation requested when saving edits to existing rows (or reordering)
  // on a yard that is no longer in preparation.
  const [pendingModification, setPendingModification] = useState<{
    description: string;
    run: () => void | Promise<void>;
  } | null>(null);

  // `loading` is only true for the initial fetch (its useState default).
  // Refetches after a save/delete/reorder update the table in place: flipping
  // `loading` again would unmount the table for a "Chargement..." placeholder
  // and lose the user's scroll position.
  const fetchData = useCallback(async () => {
    const [lotsRes, performancesRes] = await Promise.all([
      apiClient.GET("/api/admin/yards/lots", { params: { query: { yardId } } }),
      apiClient.GET("/api/admin/yards/performances", {
        params: { query: { yardId } },
      }),
    ]);

    if (lotsRes.error || performancesRes.error) {
      setError("Erreur lors du chargement des lots");
      setLoading(false);
      return;
    }

    const performancesByLot = new Map<number, LotPerformance[]>();
    for (const performance of performancesRes.data.data.performances) {
      const list = performancesByLot.get(performance.lotId) ?? [];
      list.push({
        id: performance.id,
        lotId: performance.lotId,
        name: performance.name,
        order: performance.order,
        interventionCount: performance.interventionCount,
      });
      performancesByLot.set(performance.lotId, list);
    }

    const merged: Lot[] = lotsRes.data.data.lots.map((lot) => ({
      id: lot.id,
      name: lot.name,
      shortName: lot.shortName,
      nLot: lot.nLot,
      performances: sortPerformances(performancesByLot.get(lot.id) ?? []),
    }));

    setLots(sortLots(merged));
    setError(null);
    setLoading(false);
    // Refreshed data is the new baseline: any pending draft is now stale.
    setDraftLots(new Map());
    setDraftPerformances(new Map());
    setNewLots([]);
    setNewPerformancesByLot(new Map());
  }, [yardId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Runs a modification immediately while in preparation, otherwise asks for
  // confirmation first.
  const guardModification = useCallback(
    (description: string, run: () => void | Promise<void>) => {
      if (inPreparation) {
        void run();
        return;
      }
      setPendingModification({ description, run });
    },
    [inPreparation],
  );

  // --- Draft edits to existing lots ---

  const getLotDraft = (lot: Lot): LotDraft =>
    draftLots.get(lot.id) ?? {
      nLot: lot.nLot ?? "",
      name: lot.name,
      shortName: lot.shortName ?? "",
    };

  const isLotDirty = (lot: Lot): boolean => draftLots.has(lot.id);

  const onLotFieldChange = (lot: Lot, patch: Partial<LotDraft>) => {
    setDraftLots((prev) => {
      const current = prev.get(lot.id) ?? {
        nLot: lot.nLot ?? "",
        name: lot.name,
        shortName: lot.shortName ?? "",
      };
      const next = { ...current, ...patch };
      const updated = new Map(prev);
      if (
        next.nLot === (lot.nLot ?? "") &&
        next.name === lot.name &&
        next.shortName === (lot.shortName ?? "")
      ) {
        updated.delete(lot.id);
      } else {
        updated.set(lot.id, next);
      }
      return updated;
    });
  };

  // --- Draft edits to existing performances ---

  const getPerformanceDraft = (performance: LotPerformance): PerformanceDraft =>
    draftPerformances.get(performance.id) ?? { name: performance.name };

  const isPerformanceDirty = (performance: LotPerformance): boolean =>
    draftPerformances.has(performance.id);

  const onPerformanceFieldChange = (
    performance: LotPerformance,
    patch: Partial<PerformanceDraft>,
  ) => {
    setDraftPerformances((prev) => {
      const current = prev.get(performance.id) ?? { name: performance.name };
      const next = { ...current, ...patch };
      const updated = new Map(prev);
      if (next.name === performance.name) {
        updated.delete(performance.id);
      } else {
        updated.set(performance.id, next);
      }
      return updated;
    });
  };

  // --- New (not yet created) rows ---

  const onAddLotRow = () => {
    setNewLots((prev) => [
      ...prev,
      { tempId: generateTempId(), nLot: "", name: "", shortName: "" },
    ]);
  };

  const onNewLotFieldChange = (tempId: string, patch: Partial<LotDraft>) => {
    setNewLots((prev) =>
      prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
    );
  };

  const onRemoveNewLot = (tempId: string) => {
    setNewLots((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const onAddPerformanceRow = (lotId: number) => {
    setNewPerformancesByLot((prev) => {
      const updated = new Map(prev);
      const list = updated.get(lotId) ?? [];
      updated.set(lotId, [...list, { tempId: generateTempId(), name: "" }]);
      return updated;
    });
  };

  const onNewPerformanceFieldChange = (
    lotId: number,
    tempId: string,
    patch: Partial<PerformanceDraft>,
  ) => {
    setNewPerformancesByLot((prev) => {
      const updated = new Map(prev);
      const list = updated.get(lotId) ?? [];
      updated.set(
        lotId,
        list.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
      );
      return updated;
    });
  };

  const onRemoveNewPerformance = (lotId: number, tempId: string) => {
    setNewPerformancesByLot((prev) => {
      const updated = new Map(prev);
      const list = updated.get(lotId) ?? [];
      updated.set(
        lotId,
        list.filter((row) => row.tempId !== tempId),
      );
      return updated;
    });
  };

  const hasNewRows =
    newLots.length > 0 ||
    [...newPerformancesByLot.values()].some((rows) => rows.length > 0);
  const hasExistingEdits = draftLots.size > 0 || draftPerformances.size > 0;
  const isDirty = hasNewRows || hasExistingEdits;

  useUnsavedChangesGuard(isDirty);

  const cancelDraft = () => {
    setDraftLots(new Map());
    setDraftPerformances(new Map());
    setNewLots([]);
    setNewPerformancesByLot(new Map());
  };

  const saveAll = async () => {
    setIsSaving(true);
    let failed = false;

    for (const [id, d] of draftLots.entries()) {
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/lots/{id}",
        {
          params: { path: { id } },
          body: { name: d.name, shortName: d.shortName, nLot: d.nLot },
        },
      );
      if (apiError) failed = true;
    }

    for (const [id, d] of draftPerformances.entries()) {
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/performances/{id}",
        { params: { path: { id } }, body: { name: d.name } },
      );
      if (apiError) failed = true;
    }

    for (const row of newLots) {
      const { error: apiError } = await apiClient.POST(
        "/api/admin/yards/lots",
        {
          body: {
            yardId,
            name: row.name,
            shortName: row.shortName,
            nLot: row.nLot,
          },
        },
      );
      if (apiError) failed = true;
    }

    for (const [lotId, rows] of newPerformancesByLot.entries()) {
      for (const row of rows) {
        const { error: apiError } = await apiClient.POST(
          "/api/admin/yards/performances",
          { body: { yardId, lotId, name: row.name } },
        );
        if (apiError) failed = true;
      }
    }

    setIsSaving(false);
    if (failed) {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement de certaines modifications",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: "Modifications enregistrées",
      });
    }
    fetchData();
  };

  const handleSaveClick = () => {
    const incompleteLot = [...newLots, ...draftLots.values()].some(
      (row) => !row.nLot.trim() || !row.name.trim() || !row.shortName.trim(),
    );
    const incompletePerformance = [
      ...[...newPerformancesByLot.values()].flat(),
      ...draftPerformances.values(),
    ].some((row) => !row.name.trim());
    if (incompleteLot || incompletePerformance) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de renseigner les cases marquées « Requis » avant d'enregistrer.",
      });
      return;
    }

    if (hasExistingEdits) {
      guardModification("l'enregistrement de ces modifications", saveAll);
    } else {
      void saveAll();
    }
  };

  const reorderPerformances = (lotId: number, orderedIds: number[]) => {
    guardModification("le réordonnancement des prestations", async () => {
      setLots((prev) =>
        prev.map((l) => {
          if (l.id !== lotId) return l;
          const byId = new Map(l.performances.map((p) => [p.id, p]));
          const reordered = orderedIds
            .map((id) => byId.get(id))
            .filter((p): p is LotPerformance => isNotNil(p));
          return { ...l, performances: reordered };
        }),
      );
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/performances/reorder",
        { body: { lotId, orderedIds } },
      );
      if (apiError) {
        toaster.error({
          title: "Erreur",
          description: "Échec du réordonnancement des prestations",
        });
        fetchData();
      }
    });
  };

  const confirmDeleteLot = async () => {
    if (!lotToDelete) return;
    setIsDeleting(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/yards/lots/{id}",
      { params: { path: { id: lotToDelete.id } } },
    );
    setIsDeleting(false);
    if (apiError) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression du lot",
      });
      return;
    }
    toaster.success({
      title: "Succès",
      description: "Lot supprimé avec succès",
    });
    setLotToDelete(null);
    fetchData();
  };

  const confirmDeletePerformance = async () => {
    if (!performanceToDelete) return;
    setIsDeleting(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/yards/performances/{id}",
      { params: { path: { id: performanceToDelete.id } } },
    );
    setIsDeleting(false);
    if (apiError) {
      toaster.error({
        title: "Erreur",
        description:
          apiError.error || "Échec de la suppression de la prestation",
      });
      return;
    }
    toaster.success({
      title: "Succès",
      description: "Prestation supprimée avec succès",
    });
    setPerformanceToDelete(null);
    fetchData();
  };

  const lotHasPerformances = (lotToDelete?.performances.length ?? 0) > 0;

  const performanceDeletion = describePerformanceDeletion(
    performanceToDelete?.name ?? "",
    performanceToDelete?.interventionCount ?? 0,
    inPreparation,
  );

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-gray-500">
        Chargement...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      {/* Always present, so the draft actions never shift the table. */}
      <div className="mb-3 flex h-9 shrink-0 items-center justify-end gap-2">
        {isDirty && (
          <>
            <span className="text-sm text-amber-600 dark:text-amber-400">
              Modifications non enregistrées
            </span>
            <Button
              outline
              onClick={() => void confirmDiscardDraft(cancelDraft)}
              disabled={isSaving}
            >
              Annuler
            </Button>
            <Button onClick={handleSaveClick} disabled={isSaving}>
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </>
        )}
      </div>

      <YardLotsTable
        lots={lots}
        getLotDraft={getLotDraft}
        isLotDirty={isLotDirty}
        onLotFieldChange={onLotFieldChange}
        onDeleteLot={(l) => setLotToDelete(l)}
        getPerformanceDraft={getPerformanceDraft}
        isPerformanceDirty={isPerformanceDirty}
        onPerformanceFieldChange={onPerformanceFieldChange}
        onDeletePerformance={(p) => setPerformanceToDelete(p)}
        onReorderPerformances={reorderPerformances}
        newLots={newLots}
        onNewLotFieldChange={onNewLotFieldChange}
        onRemoveNewLot={onRemoveNewLot}
        onAddLotRow={onAddLotRow}
        newPerformancesByLot={newPerformancesByLot}
        onNewPerformanceFieldChange={onNewPerformanceFieldChange}
        onRemoveNewPerformance={onRemoveNewPerformance}
        onAddPerformanceRow={onAddPerformanceRow}
      />

      {/* Lot deletion: two distinct outcomes. */}
      <Modal.Root
        open={isNotNil(lotToDelete)}
        onOpenChange={(details) => {
          if (!details.open) setLotToDelete(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>
                  {lotHasPerformances
                    ? "Supprimez d'abord les prestations"
                    : "Confirmer la suppression"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  {lotHasPerformances
                    ? `Le lot « ${lotToDelete?.name} » contient des prestations. Supprimez les prestations de ce lot pour pouvoir ensuite le supprimer.`
                    : `Vous êtes sur le point de supprimer le lot « ${lotToDelete?.name} ».`}
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setLotToDelete(null)}>
                  {lotHasPerformances ? "Fermer" : "Annuler"}
                </Button>
                {!lotHasPerformances && (
                  <Button
                    color="red"
                    onClick={confirmDeleteLot}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Suppression..." : "Supprimer"}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Performance deletion */}
      <Modal.Root
        open={isNotNil(performanceToDelete)}
        onOpenChange={(details) => {
          if (!details.open) setPerformanceToDelete(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>{performanceDeletion.title}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  {performanceDeletion.message}
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setPerformanceToDelete(null)}>
                  {performanceDeletion.canDelete ? "Annuler" : "Fermer"}
                </Button>
                {performanceDeletion.canDelete && (
                  <Button
                    color="red"
                    onClick={confirmDeletePerformance}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Suppression..." : "Supprimer"}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Confirmation when saving edits or reordering on a yard no longer in preparation. */}
      <Modal.Root
        open={isNotNil(pendingModification)}
        onOpenChange={(details) => {
          if (!details.open) setPendingModification(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la modification</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Le chantier n'est plus en période de préparation.
                  Confirmez-vous {pendingModification?.description} ?
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setPendingModification(null)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => {
                    const action = pendingModification?.run;
                    setPendingModification(null);
                    void action?.();
                  }}
                >
                  Enregistrer
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}
